import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { TrendingUp, Heart, Palette, Users, ExternalLink } from 'lucide-react';
import { LazyImage } from './ui/lazy-image';
import { Button } from '@/components/ui/button';
import g2Rank1Image from "@/assets/g2-rank1.webp";
import g2Leader from "@/assets/badges/g2-leader.svg";
import g2BestResults from "@/assets/badges/g2-best-results.svg";
import g2HighPerformer from "@/assets/badges/g2-high-performer.svg";
import g2MomentumLeader from "@/assets/badges/g2-momentum-leader.svg";
import g2BestROI from "@/assets/badges/g2-best-roi.svg";
const features = [{
  title: 'Actually Gets Used',
  subtitle: '90%+ engagement from day one',
  description: 'Employee recognition your team actually adopts.',
  icon: TrendingUp,
  image: '/lovable-uploads/884d9db5-651f-479a-a4b2-fb4ea2baeba3.png'
}, {
  title: 'Enhances Your Culture',
  subtitle: 'Logo, values, rewards — all yours',
  description: 'Customize everything from the platform UI to your reward store to match your identity.',
  icon: Palette,
  image: '/lovable-uploads/4ec40ebb-1abc-49af-bcdb-48cb505db737.png'
}, {
  title: 'Built for Retention',
  subtitle: 'Retain your best with everyday appreciation',
  description: 'Recognition drives loyalty.',
  icon: Heart,
  image: '/lovable-uploads/ba8056e3-9807-45be-a887-da39d805112d.png'
}, {
  title: 'Real People, Real Help',
  subtitle: 'Personalized onboarding + support',
  description: 'Real humans. Real help. Right when you need it.',
  icon: Users,
  image: '/lovable-uploads/307e7273-1d42-4bf8-a945-6eb554580989.png'
}];
const FeatureTabItem = ({
  feature,
  index,
  isActive,
  onSelect
}: {
  feature: typeof features[0];
  index: number;
  isActive: boolean;
  onSelect: () => void;
}) => {
  const IconComponent = feature.icon;

  // Define colors for each card based on index
  const getCardColors = (index: number, isActive: boolean) => {
    const colorSchemes = [
    // Blue scheme (moved to first position)
    {
      active: 'bg-blue-50 shadow-lg shadow-blue-100/30 transform scale-[1.02] border border-blue-150/50',
      inactive: 'bg-white hover:bg-blue-25 border border-gray-100 hover:border-blue-150 hover:shadow-md',
      iconBg: isActive ? 'bg-white shadow-md border border-blue-150/50' : 'bg-blue-50 group-hover:bg-blue-100',
      iconColor: isActive ? 'text-blue-500' : 'text-blue-500',
      textColor: isActive ? 'text-blue-600' : 'text-blue-500'
    },
    // Orange scheme (moved to second position)
    {
      active: 'bg-orange-50 shadow-lg shadow-orange-100/30 transform scale-[1.02] border border-orange-150/50',
      inactive: 'bg-white hover:bg-orange-25 border border-gray-100 hover:border-orange-150 hover:shadow-md',
      iconBg: isActive ? 'bg-white shadow-md border border-orange-150/50' : 'bg-orange-50 group-hover:bg-orange-100',
      iconColor: isActive ? 'text-orange-500' : 'text-orange-500',
      textColor: isActive ? 'text-orange-600' : 'text-orange-500'
    },
    // Yellow scheme (stays in third position)
    {
      active: 'bg-yellow-50 shadow-lg shadow-yellow-100/30 transform scale-[1.02] border border-yellow-150/50',
      inactive: 'bg-white hover:bg-yellow-25 border border-gray-100 hover:border-yellow-150 hover:shadow-md',
      iconBg: isActive ? 'bg-white shadow-md border border-yellow-150/50' : 'bg-yellow-50 group-hover:bg-yellow-100',
      iconColor: isActive ? 'text-yellow-500' : 'text-yellow-500',
      textColor: isActive ? 'text-yellow-600' : 'text-yellow-500'
    },
    // Light Pink scheme (stays in fourth position)
    {
      active: 'bg-pink-50 shadow-lg shadow-pink-100/30 transform scale-[1.02] border border-pink-150/50',
      inactive: 'bg-white hover:bg-pink-25 border border-gray-100 hover:border-pink-150 hover:shadow-md',
      iconBg: isActive ? 'bg-white shadow-md border border-pink-150/50' : 'bg-pink-50 group-hover:bg-pink-100',
      iconColor: isActive ? 'text-pink-500' : 'text-pink-500',
      textColor: isActive ? 'text-pink-600' : 'text-pink-500'
    }];
    return colorSchemes[index] || colorSchemes[0];
  };
  const colors = getCardColors(index, isActive);
  return <motion.button className={`w-full text-left p-3 sm:p-4 rounded-2xl transition-all duration-500 group relative overflow-visible ${isActive ? colors.active : colors.inactive}`} onClick={onSelect} initial={{
    opacity: 0,
    x: -20
  }} whileInView={{
    opacity: 1,
    x: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: index * 0.1,
    duration: 0.5
  }} whileHover={{
    scale: 1.02,
    transition: {
      duration: 0.2
    }
  }}>
        {/* Connection flow for active tab */}
        {isActive && (
          <div className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-full hidden lg:block z-30 w-8">
            <div className={`w-full h-1 rounded-full ${
              index === 0 ? 'bg-gradient-to-r from-blue-300 to-blue-100' : 
              index === 1 ? 'bg-gradient-to-r from-orange-300 to-orange-100' : 
              index === 2 ? 'bg-gradient-to-r from-yellow-300 to-yellow-100' : 
              'bg-gradient-to-r from-pink-300 to-pink-100'
            } opacity-60`}></div>
          </div>
        )}
       
       <div className="flex items-start gap-3 sm:gap-4 relative z-10">
         <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-500 ${colors.iconBg}`}>
           <IconComponent className={`w-6 h-6 sm:w-7 sm:h-7 transition-all duration-500 ${colors.iconColor}`} />
         </div>
         <div className="flex-1 min-w-0">
           <h3 className={`text-base sm:text-lg font-bold mb-2 leading-tight transition-all duration-500 ${isActive ? 'text-gray-900' : 'text-gray-900'}`}>
             {feature.title}
           </h3>
            <p className="text-xs sm:text-sm font-medium transition-all duration-500 text-gray-700">
              {feature.subtitle}
            </p>
         </div>
       </div>
     </motion.button>;
};
const FeatureContent = ({
  feature,
  index
}: {
  feature: typeof features[0];
  index: number;
}) => {
  // Define background colors for each feature
  const getContentColors = (index: number) => {
    const colorSchemes = [
    // Blue scheme (matching left card intensity)
    {
      background: 'bg-blue-50',
      border: 'border-blue-200/30',
      shadow: 'shadow-blue-100/50',
      overlay: 'bg-blue-100/20',
      accent: 'from-blue-200/20 to-blue-300/20',
      badge: 'bg-white/60 backdrop-blur-sm border-blue-200/30'
    },
    // Orange scheme (matching left card intensity)
    {
      background: 'bg-orange-50',
      border: 'border-orange-200/30',
      shadow: 'shadow-orange-100/50',
      overlay: 'bg-orange-100/20',
      accent: 'from-orange-200/20 to-orange-300/20',
      badge: 'bg-white/60 backdrop-blur-sm border-orange-200/30'
    },
    // Yellow scheme (matching left card intensity)
    {
      background: 'bg-yellow-50',
      border: 'border-yellow-200/30',
      shadow: 'shadow-yellow-100/50',
      overlay: 'bg-yellow-100/20',
      accent: 'from-yellow-200/20 to-yellow-300/20',
      badge: 'bg-white/60 backdrop-blur-sm border-yellow-200/30'
    },
    // Light Pink scheme (matching left card intensity)
    {
      background: 'bg-pink-50',
      border: 'border-pink-200/30',
      shadow: 'shadow-pink-100/50',
      overlay: 'bg-pink-100/20',
      accent: 'from-pink-200/20 to-pink-300/20',
      badge: 'bg-white/60 backdrop-blur-sm border-pink-200/30'
    }];
    return colorSchemes[index] || colorSchemes[0];
  };
  const colors = getContentColors(index);
  
  return (
    <motion.div 
      key={feature.title} 
      className={`${colors.background} rounded-3xl p-6 sm:p-8 lg:p-12 relative overflow-hidden h-[28rem] md:h-[32rem] lg:h-96 flex flex-col justify-between border ${colors.border} shadow-lg ${colors.shadow}`} 
      initial={{
        opacity: 0,
        scale: 0.95
      }} 
      animate={{
        opacity: 1,
        scale: 1
      }} 
      transition={{
        duration: 0.5
      }}
    >
      {/* Subtle background elements */}
      <div className={`absolute inset-0 ${colors.overlay}`}></div>
      <div className={`absolute top-4 right-4 w-20 h-20 bg-gradient-to-br ${colors.accent} rounded-full blur-xl`}></div>
      
      {/* Split content layout */}
      <div className="relative z-10 h-full flex flex-col">
        {/* Full width heading */}
        <h3 className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed font-medium mb-4">
          {feature.title === 'Actually Gets Used' ? 'Employee recognition your team actually adopts' :
           feature.title === 'Enhances Your Culture' ? 'Recognition platform that reflects your brand' : 
           feature.title === 'Built for Retention' ? 'Appreciation that builds loyalty' : 
           feature.title === 'Real People, Real Help' ? 'Human support when it matters' :
           feature.description}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 h-full">
          {/* Left side - Text content */}
          <div className="flex flex-col justify-center">
            <div className={`inline-flex items-center px-3 py-1.5 rounded-full border mb-3 w-fit ${
              index === 0 ? 'bg-gradient-to-r from-blue-500 to-blue-600 border-blue-600' : 
              index === 1 ? 'bg-gradient-to-r from-orange-500 to-orange-600 border-orange-600' : 
              index === 2 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 border-yellow-600' : 
              'bg-gradient-to-r from-purple-500 to-purple-600 border-purple-600'
            }`}>
              <span className={`text-xs font-semibold ${
                index === 0 ? 'text-white' : 
                index === 1 ? 'text-white' : 
                index === 2 ? 'text-white' : 
                'text-white'
              }`}>
                {feature.subtitle}
              </span>
            </div>
            {feature.title === 'Real People, Real Help' && (
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                Our platform onboarding and customer success experts partner with you to ensure smooth software adoption, quick rollout, and measurable impact. With real people guiding every step, you get the support to scale recognition successfully—right when you need it.
              </p>
            )}
            {feature.title === 'Built for Retention' && (
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                Employee recognition is one of the strongest drivers of retention. By celebrating milestones and everyday wins, you reduce turnover, boost loyalty, and keep top talent engaged for the long term.
              </p>
            )}
            {feature.title === 'Enhances Your Culture' && (
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                Tailor everything—logos, values, colors, and your rewards store—to match your company's identity. Employees experience recognition as an authentic extension of your culture, not just another HR platform.
              </p>
            )}
            {/* Dummy text for other features */}
            {feature.title !== 'Real People, Real Help' && feature.title !== 'Built for Retention' && feature.title !== 'Enhances Your Culture' && (
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                From shoutouts to boosts and rewards, Assembly makes recognition natural and fun. With 90%+ participation, appreciation becomes part of everyday work—not an afterthought.
              </p>
            )}
          </div>
          
          {/* Right side - Image */}
          <div className="flex items-center justify-center flex-1 min-h-0 overflow-hidden">
            <LazyImage 
              src={feature.image} 
              alt={feature.title} 
              className="max-w-full max-h-[275px] w-auto h-auto object-contain" 
              showSkeleton={true} 
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};
const CoreFeatures = () => {
  const sectionRef = useRef(null);
  const [activeFeature, setActiveFeature] = useState(0);
  const isInView = useInView(sectionRef, {
    once: true,
    amount: 0.2,
    margin: "0px 0px -100px 0px"
  });
  return <section ref={sectionRef} className="bg-gradient-to-b from-white to-gray-50/30 py-12 2xl:py-20">
      <div className="container mx-auto px-4 2xl:px-12 max-w-none 2xl:max-w-[1600px]">
        <motion.div className="text-center mb-16" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: isInView ? 1 : 0,
        y: isInView ? 0 : 20
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }}>
          {/* Badge */}
          <motion.div className="inline-flex items-center px-4 py-2 bg-blue-100 rounded-full mb-8" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: isInView ? 1 : 0,
          scale: isInView ? 1 : 0.9
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }}>
            <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">
              WHY ASSEMBLY?
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 leading-tight max-w-4xl 2xl:max-w-6xl mx-auto" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: isInView ? 1 : 0,
          y: isInView ? 0 : 20
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }}>
            Recognition That Actually Works<br />
            for Your <span className="text-blue-600">People and Culture</span>
          </motion.h2>
          
          {/* Subheadline */}
          <motion.p className="text-sm lg:text-base text-gray-600 max-w-3xl 2xl:max-w-5xl mx-auto font-medium leading-relaxed" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: isInView ? 1 : 0,
          y: isInView ? 0 : 20
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }}>See why HR and People teams trust Assembly software to build real engagement and lasting culture — with recognition that&apos;s meaningful, personalized, and easy to scale.</motion.p>
        </motion.div>

        {/* Redesigned Layout - Tabbed Interface */}
        <div className="max-w-7xl mx-auto">
          {/* Mobile: Vertical Stacked Buttons, Desktop: Horizontal Tabs */}
          <div className="flex flex-col lg:flex-row lg:justify-center gap-2 mb-0">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              const isActive = activeFeature === index;
              
              return (
                <button
                  key={feature.title}
                  onClick={() => setActiveFeature(index)}
                  className={`relative flex items-center lg:flex-col gap-3 lg:gap-0 lg:justify-center px-4 py-3 lg:px-6 lg:py-4 rounded-2xl lg:rounded-t-2xl lg:rounded-b-none transition-colors duration-300 w-full lg:w-auto lg:min-w-[140px] ${
                    isActive 
                      ? 'bg-white border-2 lg:border-t-2 lg:border-x-2 lg:border-b-0 border-gray-200 shadow-md lg:shadow-none z-20' 
                      : 'bg-gray-100 border-2 border-transparent'
                  }`}
                >
                  {/* Icon with colored background */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 lg:mb-2 ${
                    index === 0 ? 'bg-blue-100' : 
                    index === 1 ? 'bg-orange-100' : 
                    index === 2 ? 'bg-yellow-100' : 
                    'bg-pink-100'
                  }`}>
                    <IconComponent className={`w-5 h-5 ${
                      index === 0 ? 'text-blue-600' : 
                      index === 1 ? 'text-orange-600' : 
                      index === 2 ? 'text-yellow-600' : 
                      'text-pink-600'
                    }`} />
                  </div>
                  
                  {/* Label */}
                  <span className={`text-sm font-semibold text-left lg:text-center leading-tight flex-1 lg:flex-none ${
                    isActive ? 'text-gray-900' : 'text-gray-600'
                  }`}>
                    {feature.title}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Content Area - Connected to Active Tab */}
          <div 
            key={`content-${activeFeature}`}
            className="bg-white border-2 border-gray-200 rounded-2xl p-4 sm:p-8 lg:p-12 shadow-lg mt-2 lg:-mt-[2px]"
          >
            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
              
              {/* Left Column - Text Content */}
              <div className="flex flex-col justify-center space-y-4 sm:space-y-6">
                {/* Feature Icon & Title */}
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      activeFeature === 0 ? 'bg-blue-100' : 
                      activeFeature === 1 ? 'bg-orange-100' : 
                      activeFeature === 2 ? 'bg-yellow-100' : 
                      'bg-pink-100'
                    }`}>
                      {React.createElement(features[activeFeature].icon, { 
                        className: `w-5 h-5 sm:w-6 sm:h-6 ${
                          activeFeature === 0 ? 'text-blue-600' : 
                          activeFeature === 1 ? 'text-orange-600' : 
                          activeFeature === 2 ? 'text-yellow-600' : 
                          'text-pink-600'
                        }` 
                      })}
                    </div>
                    <h3 className="text-lg sm:text-2xl font-bold text-gray-900 leading-tight">
                      {features[activeFeature].title}
                    </h3>
                  </div>
                  
                  {/* Subtitle Badge */}
                  <div className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold w-fit ${
                    activeFeature === 0 ? 'bg-blue-100 text-blue-700' : 
                    activeFeature === 1 ? 'bg-orange-100 text-orange-700' : 
                    activeFeature === 2 ? 'bg-yellow-100 text-yellow-700' : 
                    'bg-pink-100 text-pink-700'
                  }`}>
                    {features[activeFeature].subtitle}
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                  {features[activeFeature].title === 'Actually Gets Used' ? 
                    'Employee recognition your team actually adopts. From shoutouts to boosts and rewards, Assembly makes recognition natural and fun. With 90%+ participation, appreciation becomes part of everyday work—not an afterthought.' :
                   features[activeFeature].title === 'Enhances Your Culture' ? 
                    'Recognition platform that reflects your brand. Tailor everything—logos, values, colors, and your rewards store—to match your company\'s identity. Employees experience recognition as an authentic extension of your culture, not just another HR platform.' : 
                   features[activeFeature].title === 'Built for Retention' ? 
                    'Appreciation that builds loyalty. Employee recognition is one of the strongest drivers of retention. By celebrating milestones and everyday wins, you reduce turnover, boost loyalty, and keep top talent engaged for the long term.' : 
                   features[activeFeature].title === 'Real People, Real Help' ? 
                    'Human support when it matters. Our platform onboarding and customer success experts partner with you to ensure smooth software adoption, quick rollout, and measurable impact. With real people guiding every step, you get the support to scale recognition successfully—right when you need it.' :
                    features[activeFeature].description}
                </p>

                {/* Additional Benefits */}
                <div className="space-y-2 sm:space-y-3">
                  {features[activeFeature].title === 'Actually Gets Used' && (
                    <>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="w-2 h-2 rounded-full bg-blue-500 mt-1 sm:mt-1.5 flex-shrink-0"></div>
                        <span className="text-gray-700 text-xs sm:text-sm">90%+ engagement from day one</span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="w-2 h-2 rounded-full bg-blue-500 mt-1 sm:mt-1.5 flex-shrink-0"></div>
                        <span className="text-gray-700 text-xs sm:text-sm">Intuitive interface that requires no training</span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="w-2 h-2 rounded-full bg-blue-500 mt-1 sm:mt-1.5 flex-shrink-0"></div>
                        <span className="text-gray-700 text-xs sm:text-sm">Mobile-first design for recognition on-the-go</span>
                      </div>
                    </>
                  )}
                  {features[activeFeature].title === 'Enhances Your Culture' && (
                    <>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="w-2 h-2 rounded-full bg-orange-500 mt-1 sm:mt-1.5 flex-shrink-0"></div>
                        <span className="text-gray-700 text-xs sm:text-sm">Complete brand customization</span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="w-2 h-2 rounded-full bg-orange-500 mt-1 sm:mt-1.5 flex-shrink-0"></div>
                        <span className="text-gray-700 text-xs sm:text-sm">Integrate company values into recognition</span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="w-2 h-2 rounded-full bg-orange-500 mt-1 sm:mt-1.5 flex-shrink-0"></div>
                        <span className="text-gray-700 text-xs sm:text-sm">Custom reward store with your preferred vendors</span>
                      </div>
                    </>
                  )}
                  {features[activeFeature].title === 'Built for Retention' && (
                    <>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="w-2 h-2 rounded-full bg-yellow-500 mt-1 sm:mt-1.5 flex-shrink-0"></div>
                        <span className="text-gray-700 text-xs sm:text-sm">Reduce turnover by up to 31%</span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="w-2 h-2 rounded-full bg-yellow-500 mt-1 sm:mt-1.5 flex-shrink-0"></div>
                        <span className="text-gray-700 text-xs sm:text-sm">Build emotional connection to your company</span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="w-2 h-2 rounded-full bg-yellow-500 mt-1 sm:mt-1.5 flex-shrink-0"></div>
                        <span className="text-gray-700 text-xs sm:text-sm">Celebrate milestones and everyday wins</span>
                      </div>
                    </>
                  )}
                  {features[activeFeature].title === 'Real People, Real Help' && (
                    <>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="w-2 h-2 rounded-full bg-pink-500 mt-1 sm:mt-1.5 flex-shrink-0"></div>
                        <span className="text-gray-700 text-xs sm:text-sm">Dedicated customer success manager</span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="w-2 h-2 rounded-full bg-pink-500 mt-1 sm:mt-1.5 flex-shrink-0"></div>
                        <span className="text-gray-700 text-xs sm:text-sm">Personalized onboarding and training</span>
                      </div>
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="w-2 h-2 rounded-full bg-pink-500 mt-1 sm:mt-1.5 flex-shrink-0"></div>
                        <span className="text-gray-700 text-xs sm:text-sm">24/7 support when you need it</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Right Column - Image */}
              <div className="flex items-center justify-center mt-6 lg:mt-0">
                <motion.div 
                  key={`image-${activeFeature}`}
                  className={`rounded-2xl overflow-hidden w-full max-w-md ${
                    activeFeature === 0 ? 'bg-gradient-to-br from-blue-50 to-blue-100' : 
                    activeFeature === 1 ? 'bg-gradient-to-br from-orange-50 to-orange-100' : 
                    activeFeature === 2 ? 'bg-gradient-to-br from-yellow-50 to-yellow-100' : 
                    'bg-gradient-to-br from-pink-50 to-pink-100'
                  }`}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="p-4 sm:p-6">
                    <LazyImage 
                      src={features[activeFeature].image} 
                      alt={features[activeFeature].title} 
                      className="w-full h-auto object-contain" 
                      showSkeleton={true} 
                    />
                  </div>
                </motion.div>
              </div>

            </div>
          </div>
        </div>

        {/* G2 Recognition Section */}
        <div className="mt-16 lg:mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left Column - Text and Badges */}
              <div className="space-y-6">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground">
                  Ranked <span className="text-primary">#1 by our customers</span>... for a reason.
                </h2>
                
                <p className="text-base text-muted-foreground leading-relaxed">
                  Assembly is ranked the #1 Employee Recognition & Rewards Platform on G2. Customers love how simple it is to use, how fast it is to set up, and how consistently it delivers meaningful results. With the highest ROI in recognition software, Assembly is trusted by small businesses, mid-market leaders, and enterprise organizations worldwide.
                </p>

                {/* Badge Lineup - Desktop */}
                <div className="hidden sm:flex items-center justify-start gap-3 flex-wrap">
                  <img
                    src={g2MomentumLeader}
                    alt="G2 Momentum Leader"
                    className="h-20 w-auto hover:scale-105 transition-transform duration-300"
                  />
                  <img
                    src={g2Leader}
                    alt="G2 Leader"
                    className="h-24 w-auto hover:scale-105 transition-transform duration-300"
                  />
                  <img
                    src={g2BestResults}
                    alt="G2 Best Results"
                    className="h-24 w-auto hover:scale-105 transition-transform duration-300"
                  />
                  <img
                    src={g2HighPerformer}
                    alt="G2 High Performer"
                    className="h-24 w-auto hover:scale-105 transition-transform duration-300"
                  />
                  <img
                    src={g2BestROI}
                    alt="G2 Best ROI"
                    className="h-20 w-auto hover:scale-105 transition-transform duration-300"
                  />
                </div>

                {/* Badge Lineup - Mobile (Horizontal Scroll) */}
                <div className="sm:hidden overflow-x-auto pb-4">
                  <div className="flex items-center gap-3 min-w-max px-2">
                    <img src={g2MomentumLeader} alt="G2 Momentum Leader" className="h-16 w-auto" />
                    <img src={g2Leader} alt="G2 Leader" className="h-20 w-auto" />
                    <img src={g2BestResults} alt="G2 Best Results" className="h-20 w-auto" />
                    <img src={g2HighPerformer} alt="G2 High Performer" className="h-20 w-auto" />
                    <img src={g2BestROI} alt="G2 Best ROI" className="h-16 w-auto" />
                  </div>
                </div>

                {/* CTA Button */}
                <div className="pt-2">
                  <Button
                    asChild
                    variant="outline"
                    size="lg"
                    className="font-semibold border-2 hover:bg-primary hover:text-white hover:border-primary transition-all duration-300"
                  >
                    <a
                      href="https://www.g2.com/categories/employee-recognition?tab=easiest_to_use"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2"
                    >
                      <span>Check it out in G2</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                </div>
              </div>

              {/* Right Column - Visual */}
              <div className="space-y-3 max-w-xl mx-auto lg:mx-0">
                <img
                  src={g2Rank1Image}
                  alt="Assembly ranked #1 on G2 for Usability"
                  className="w-full h-auto rounded-2xl"
                />
                <p className="text-xs text-center text-muted-foreground font-medium">
                  Assembly outperforms top recognition platforms - ranked #1
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default CoreFeatures;